/**
 * 科技人才新版配置（设计稿 科技人才_slices / 科技人才-详情_slices）
 * 列表图：talent_new/aliases；详情图：talent_new/detail_aliases
 *
 * 列表切图映射（aliases ← 科技人才_slices）:
 * banner-bg ← beijing@2x；title-mark ← 科技人才@2x
 * icon-search ← 搜索；icon-field ← 领域服务；icon-region ← icon_地区
 * icon-views ← 浏览；icon-publish ← 发布；icon-transform ← 转化
 * name-deco ← 矩形 906；card-bg ← 矩形 907；avatar-default ← 图层 1
 *
 * 详情切图映射（detail_aliases ← 科技人才-详情_slices）:
 * avatar-default ← 图层 7；similar-avatar ← 图层 8；similar-card-bg ← 组 29
 * icon-star ← 多边形 1；icon-collect ← 收藏；icon-views ← 浏览
 * icon-location ← 地图；icon-field ← 领域服务；icon-coop ← 合作
 * icon-transfer ← token-swap-fill；card-bg ← huaban；section-bar ← 矩形 914
 */
import { deepCloneConfig } from '@/utils/deepCloneConfig'
import { applyPortalZhToRu } from '@/utils/applyPortalZhToRu'
import { getCurrentLocale } from '@/utils/i18nText'
import { L, resolveLocaleImages } from '@/utils/localeAsset'

const A = (name) => L(`talent_new/aliases/${name}`)
const D = (name) => L(`talent_new/detail_aliases/${name}`)
const Nav = (name) => L(`home_new/aliases/${name}`)

const zhCN = {
  siteInfo: {
    title: '杨凌农业科技成果转化综合信息服务平台',
    logo: L('shouye/web-logo.png'),
    loginText: '登录',
    registerText: '注册',
    logoutText: '退出'
  },
  breadcrumb: {
    home: '首页',
    current: '科技人才'
  },
  header: {
    titleMain1: '科技',
    titleMain2: '人才',
    titleSub: '赋能科技创新 助力成果转化',
    searchPlaceholder: '请输入关键词进行搜索',
    searchButton: '搜索',
    bannerBg: A('banner-bg.png')
  },
  modules: [
    { id: 'home', name: '首页', enName: 'HOME', icon: Nav('nav-home.png'), link: '/index' },
    { id: 'innovation', name: '走进杨凌', enName: 'YANGLING', icon: Nav('nav-innovation.png'), link: '/innovation' },
    { id: 'supply', name: '供需中心', enName: 'SUPPLY & DEMAND', icon: Nav('nav-supply.png'), link: '/supply-demand' },
    { id: 'evaluation', name: '价值评估', enName: 'EVALUATE', icon: Nav('nav-evaluation.png'), link: '/value-assessment' },
    { id: 'manager', name: '技术经理人', enName: 'MANAGER', icon: Nav('nav-manager.png'), link: '/manager' },
    { id: 'transaction', name: '交易中心', enName: 'TRANSACTION', icon: Nav('nav-transaction.png'), link: '/transaction' },
    { id: 'service', name: '科创服务', enName: 'SERVICE', icon: Nav('nav-service.png'), link: '/innovation-service' },
    { id: 'talent', name: '科技人才', enName: 'TALENT', icon: Nav('nav-talent.png'), link: '/talent' },
    { id: 'enterprise', name: '农业企业', enName: 'ENTERPRISE', icon: Nav('nav-enterprise.png'), link: '/agriculture-enterprise' }
  ],
  icons: {
    search: A('icon-search.png'),
    field: A('icon-field.png'),
    region: A('icon-region.png'),
    views: A('icon-views.png'),
    publish: A('icon-publish.png'),
    transform: A('icon-transform.png'),
    nameDeco: A('name-deco.png'),
    breadcrumbPin: A('icon-region.png')
  },
  fieldFilter: {
    label: '所属领域：',
    allLabel: '全部'
  },
  card: {
    publishLabel: '已发布成果',
    transformLabel: '已转化成果',
    emptyTip: '待接口提供',
    avatarFallback: L('default.png'),
    cardBg: A('card-bg.png')
  },
  pagination: {
    pageInfo: '共{total}条，每页显示{pageSize}条',
    prevPage: '上一页',
    nextPage: '下一页',
    loading: '加载中...'
  },
  footer: {
    copyright: '© 2026 杨凌农业科技成果转化综合信息服务平台 版权所有',
    support: '技术支持：浪潮软件'
  },
  talentDetail: {
    breadcrumb: {
      home: '首页',
      list: '科技人才',
      current: '科技人才信息'
    },
    banner: {
      viewsLabel: '浏览量：',
      locationLabel: '所在地：',
      collect: '收藏',
      collected: '已收藏',
      backgroundImage: D('banner-bg.png'),
      avatarFallback: L('default.png'),
      iconStar: D('icon-star.png'),
      iconCollect: D('icon-collect.png'),
      iconViews: D('icon-views.png'),
      iconLocation: D('icon-location.png')
    },
    info: {
      title: '基本信息',
      subTitle: 'BASIC INFORMATION',
      workplace: '工作单位',
      education: '学历',
      profTitle: '职称',
      phone: '联系电话'
    },
    profile: {
      title: '个人简介',
      subTitle: 'PERSONAL PROFILE'
    },
    registeredAchievements: {
      title: '已登记的成果',
      subTitle: 'REGISTERED ACHIEVEMENTS',
      moreText: '更多+',
      fieldLabel: '所属领域：',
      transferLabel: '转化方式：',
      viewsLabel: '浏览：',
      cardBg: D('card-bg.png')
    },
    convertedAchievements: {
      title: '已转化的成果',
      subTitle: 'CONVERTED ACHIEVEMENTS',
      moreText: '更多+',
      fieldLabel: '所属领域：',
      transferLabel: '转化方式：',
      viewsLabel: '浏览：',
      cardBg: D('card-bg.png'),
      placeholderTip: '待接口提供'
    },
    registeredDemands: {
      title: '已登记的需求',
      subTitle: 'REGISTERED DEMANDS',
      moreText: '更多+',
      fieldLabel: '所属领域：',
      coopLabel: '合作方式：',
      viewsLabel: '浏览：',
      cardBg: D('card-bg.png')
    },
    similar: {
      title: '类似专家',
      subTitle: 'SIMILAR EXPERTS',
      avatarFallback: L('default.png'),
      cardBg: D('similar-card-bg.png')
    },
    icons: {
      field: D('icon-field.png'),
      transfer: D('icon-transfer.png'),
      coop: D('icon-coop.png'),
      views: D('icon-views.png')
    },
    emptyTip: '待接口提供'
  }
}

const enUS = {
  siteInfo: {
    title: 'Yangling Agricultural Science & Technology Achievement Transformation Platform',
    logo: L('shouye/web-logo.png'),
    loginText: 'Login',
    registerText: 'Register',
    logoutText: 'Logout'
  },
  breadcrumb: {
    home: 'Home',
    current: 'Sci-Tech Talent'
  },
  header: {
    titleMain1: 'Sci-Tech',
    titleMain2: 'Talent',
    titleSub: 'Empowering sci-tech innovation, enabling achievement commercialization',
    searchPlaceholder: 'Search by keyword',
    searchButton: 'Search',
    bannerBg: A('banner-bg.png')
  },
  modules: [
    { id: 'home', name: 'Home', enName: 'HOME', icon: Nav('nav-home.png'), link: '/index' },
    { id: 'innovation', name: 'Yangling', enName: 'YANGLING', icon: Nav('nav-innovation.png'), link: '/innovation' },
    { id: 'supply', name: 'Supply & Demand', enName: 'SUPPLY & DEMAND', icon: Nav('nav-supply.png'), link: '/supply-demand' },
    { id: 'evaluation', name: 'Evaluation', enName: 'EVALUATE', icon: Nav('nav-evaluation.png'), link: '/value-assessment' },
    { id: 'manager', name: 'Managers', enName: 'MANAGER', icon: Nav('nav-manager.png'), link: '/manager' },
    { id: 'transaction', name: 'Transaction', enName: 'TRANSACTION', icon: Nav('nav-transaction.png'), link: '/transaction' },
    { id: 'service', name: 'Services', enName: 'SERVICE', icon: Nav('nav-service.png'), link: '/innovation-service' },
    { id: 'talent', name: 'Talent', enName: 'TALENT', icon: Nav('nav-talent.png'), link: '/talent' },
    { id: 'enterprise', name: 'Enterprises', enName: 'ENTERPRISE', icon: Nav('nav-enterprise.png'), link: '/agriculture-enterprise' }
  ],
  icons: zhCN.icons,
  fieldFilter: {
    label: 'Field:',
    allLabel: 'All'
  },
  card: {
    publishLabel: 'Published',
    transformLabel: 'Transformed',
    emptyTip: '待接口提供',
    avatarFallback: L('default.png'),
    cardBg: A('card-bg.png')
  },
  pagination: {
    pageInfo: '{total} items, {pageSize} per page',
    prevPage: 'Prev',
    nextPage: 'Next',
    loading: 'Loading...'
  },
  footer: {
    copyright: '© 2026 Yangling Agricultural Science & Technology Marketplace. All rights reserved.',
    support: 'Technical support: Inspur Software'
  },
  talentDetail: {
    breadcrumb: {
      home: 'Home',
      list: 'Sci-Tech Talent',
      current: 'Talent Info'
    },
    banner: {
      viewsLabel: 'Views: ',
      locationLabel: 'Location: ',
      collect: 'Favorite',
      collected: 'Favorited',
      backgroundImage: D('banner-bg.png'),
      avatarFallback: L('default.png'),
      iconStar: D('icon-star.png'),
      iconCollect: D('icon-collect.png'),
      iconViews: D('icon-views.png'),
      iconLocation: D('icon-location.png')
    },
    info: {
      title: 'Basic Info',
      subTitle: 'BASIC INFORMATION',
      workplace: 'Organization',
      education: 'Education',
      profTitle: 'Title',
      phone: 'Phone'
    },
    profile: {
      title: 'Profile',
      subTitle: 'PERSONAL PROFILE'
    },
    registeredAchievements: {
      title: 'Registered Achievements',
      subTitle: 'REGISTERED ACHIEVEMENTS',
      moreText: 'More+',
      fieldLabel: 'Field: ',
      transferLabel: 'Transfer: ',
      viewsLabel: 'Views: ',
      cardBg: D('card-bg.png')
    },
    convertedAchievements: {
      title: 'Converted Achievements',
      subTitle: 'CONVERTED ACHIEVEMENTS',
      moreText: 'More+',
      fieldLabel: 'Field: ',
      transferLabel: 'Transfer: ',
      viewsLabel: 'Views: ',
      cardBg: D('card-bg.png'),
      placeholderTip: '待接口提供'
    },
    registeredDemands: {
      title: 'Registered Demands',
      subTitle: 'REGISTERED DEMANDS',
      moreText: 'More+',
      fieldLabel: 'Field: ',
      coopLabel: 'Cooperation: ',
      viewsLabel: 'Views: ',
      cardBg: D('card-bg.png')
    },
    similar: {
      title: 'Similar Experts',
      subTitle: 'SIMILAR EXPERTS',
      avatarFallback: L('default.png'),
      cardBg: D('similar-card-bg.png')
    },
    icons: {
      field: D('icon-field.png'),
      transfer: D('icon-transfer.png'),
      coop: D('icon-coop.png'),
      views: D('icon-views.png')
    },
    emptyTip: '待接口提供'
  }
}

export function getTechTalentNewConfig(locale = getCurrentLocale()) {
  let raw
  if (locale === 'en-US') raw = deepCloneConfig(enUS)
  else if (locale === 'ru-RU') raw = applyPortalZhToRu(deepCloneConfig(zhCN))
  else raw = deepCloneConfig(zhCN)
  return resolveLocaleImages(raw, locale)
}
